import React, { useState, useEffect, useRef } from 'react';

// Counter Animation Hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(!startOnView);
  const ref = useRef(null);

  // Handle intersection observer for scroll-triggered animation
  useEffect(() => {
    if (!startOnView || hasStarted) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [startOnView, hasStarted]);

  // Handle count animation
  useEffect(() => {
    if (!hasStarted) return;

    let startTime = null;
    let animationId;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [hasStarted, end, duration]);

  return { count, ref };
};

const SocialProofSection = () => {
  const { count: avgClaimCount, ref: avgClaimRef } = useCountUp(830, 2000);
  const { count: totalBillionCount, ref: totalBillionRef } = useCountUp(75, 2500);
  const { count: clientsCount, ref: clientsRef } = useCountUp(300000, 2000);

  // Finance lenders (the actual companies being claimed against)
  const lenders = [
    { name: 'Black Horse', logo: 'Black Horse' },
    { name: 'Santander', logo: 'Santander' },
    { name: 'Moneybarn', logo: 'Moneybarn' },
    { name: 'Close Brothers', logo: 'Close Brothers' },
    { name: 'Alphera', logo: 'Alphera' },
    { name: 'MotoNovo', logo: 'MotoNovo' },
  ];

  return (
    <section className="py-16 lg:py-20 bg-secondary/50">
      <div className="section-container">
        {/* Finance Lender Logos */}
        <div className="text-center mb-12">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-8">
            We investigate claims against major lenders including
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
            {lenders.map((lender) => (
              <div
                key={lender.name}
                className="logo-grayscale flex items-center justify-center px-6 py-3"
              >
                <span className="text-lg lg:text-xl font-bold text-muted-foreground/60 tracking-tight">
                  {lender.logo}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Counter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          <div
            ref={avgClaimRef}
            className="text-center p-8 rounded-2xl bg-card border border-border shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <p className="text-4xl lg:text-5xl font-bold text-primary counter-number mb-3">
              £{avgClaimCount}*
            </p>
            <p className="text-base text-muted-foreground font-medium">
              Average Claim Per Agreement
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">
              FCA Consumer Redress Scheme estimate
            </p>
          </div>

          <div
            ref={totalBillionRef}
            className="text-center p-8 rounded-2xl bg-card border border-border shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <p className="text-4xl lg:text-5xl font-bold text-gold counter-number mb-3">
              £{(totalBillionCount / 10).toFixed(1)}bn*
            </p>
            <p className="text-base text-muted-foreground font-medium">
              Owed by Lenders
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">
              Expected compensation scheme cost
            </p>
          </div>

          <div
            ref={clientsRef}
            className="text-center p-8 rounded-2xl bg-card border border-border shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <p className="text-4xl lg:text-5xl font-bold text-primary counter-number mb-3">
              {clientsCount.toLocaleString()}+
            </p>
            <p className="text-base text-muted-foreground font-medium">
              Claims Located
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">
              And growing every day
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export { SocialProofSection };
